import { useEffect, useState } from 'react';

const textState = new WeakMap();
const attributeState = new WeakMap();
const originalTitle = document.title;
const supplementalTranslations = {
  Login: 'تسجيل الدخول',
  'Operational Planning Portal': 'بوابة التخطيط التشغيلي',
  'Access the Sharjah Family Development workspace for your account.': 'ادخل إلى مساحة عمل مؤسسة الشارقة للتنمية الأسرية لحسابك.',
  'Work email is required.': 'البريد المؤسسي مطلوب.',
  'Enter a valid work email.': 'أدخل بريدًا مؤسسيًا صالحًا.',
  'Password is required.': 'كلمة المرور مطلوبة.',
  'Password must be at least 6 characters.': 'يجب ألا تقل كلمة المرور عن 6 أحرف.',
  'Secure access': 'دخول آمن',
  'Contact your system administrator to reset your password.': 'تواصل مع مسؤول النظام لإعادة تعيين كلمة المرور.',
  'Access is restricted to authorized users.': 'الدخول مخصص للمستخدمين المصرح لهم.',
  'Authorized account': 'حساب مصرح له',
  '2026 Planning Cycle': 'دورة التخطيط 2026',
  'Last updated': 'آخر تحديث',
  'Performance thresholds': 'حدود قياس الأداء',
  'Approved result': 'نتيجة معتمدة',
  'Previous-period data; results are reviewed during regular monitoring.': 'بيانات الفترة السابقة؛ تُراجع النتائج ضمن المتابعة الدورية.',
  'Evidence description; no file is attached.': 'وصف الشاهد؛ لا يوجد ملف مرفق.',
  'Results appear on the dashboard after Strategy Office approval. Evidence references are recorded with each result.': 'تظهر النتائج على لوحة المؤشرات بعد اعتماد مكتب الاستراتيجية، ويُسجل مرجع الشاهد مع كل نتيجة.',
  'Entries remain in this browser session. Sign out to switch accounts without refreshing the page.': 'تبقى الإدخالات في جلسة المتصفح الحالية. سجّل الخروج للتبديل بين الحسابات دون تحديث الصفحة.',
  'Operational planning and performance management system': 'منظومة التخطيط التشغيلي وإدارة الأداء',
  'This field is required.': 'هذا الحقل مطلوب.',
  'Enter a value of zero or greater.': 'أدخل قيمة تساوي صفرًا أو أكبر.',
  'Enter a value greater than zero.': 'أدخل قيمة أكبر من صفر.',
  'Enter a value from 0 to 100.': 'أدخل قيمة من 0 إلى 100.',
  'Enter a value greater than 0 and no more than 100.': 'أدخل قيمة أكبر من 0 ولا تتجاوز 100.',
  'End date must be on or after start date.': 'يجب أن يكون تاريخ النهاية مساويًا لتاريخ البداية أو بعده.',
  'Return plan with feedback': 'إعادة الخطة مع الملاحظات',
  'Return result with feedback': 'إعادة النتيجة مع الملاحظات',
  'Return plan with guidance': 'إعادة الخطة مع التوجيهات',
  'Provide clear feedback so the responsible team can make the required changes.': 'أدخل ملاحظات واضحة حتى يتمكن الفريق المسؤول من إجراء التعديلات المطلوبة.',
  'Feedback / guidance': 'الملاحظات / التوجيهات',
  'Describe the required changes and guidance…': 'صف التعديلات والتوجيهات المطلوبة…',
  'Feedback is required.': 'الملاحظات مطلوبة.',
  'Be specific and include the expected correction.': 'كن محددًا واذكر التصحيح المطلوب.',
  'Submit feedback': 'إرسال الملاحظات',
};

function makeTranslator(language) {
  if (language === 'en') return (value) => value;
  const translations = { ...(window.ARABIC_TRANSLATIONS || {}), ...supplementalTranslations };
  const entries = Object.entries(translations).sort((a, b) => b[0].length - a[0].length);
  if (!entries.length) return (value) => value;
  const escaped = entries.map(([key]) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const expression = new RegExp(`(^|[^A-Za-z])(${escaped.join('|')})(?![A-Za-z])`, 'g');
  return (value) => String(value).replace(expression, (match, prefix, key) => `${prefix}${translations[key]}`);
}

function readSavedLanguage() {
  try { return localStorage.getItem('interface-language') === 'ar' ? 'ar' : 'en'; }
  catch { return 'en'; }
}

export default function LanguageToggle() {
  const [language, setLanguage] = useState(readSavedLanguage);

  useEffect(() => {
    const translate = makeTranslator(language);
    let observer;
    const excluded = (element) => Boolean(element.closest('script,style,[data-no-translate],input,textarea'));
    const applyTranslations = () => {
      observer?.disconnect();
      document.querySelectorAll('option:not([value])').forEach((option) => { option.value = option.textContent; });
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!node.parentElement || excluded(node.parentElement) || !node.nodeValue.trim()) continue;
        const saved = textState.get(node);
        const original = saved && (node.nodeValue === saved.output || node.nodeValue === saved.original)
          ? saved.original
          : node.nodeValue;
        const output = translate(original);
        if (output !== node.nodeValue) node.nodeValue = output;
        textState.set(node, { original, output, language });
      }
      document.querySelectorAll('[placeholder],[aria-label],[title]').forEach((element) => {
        if (element.closest('[data-no-translate],script,style')) return;
        const state = attributeState.get(element) || {};
        ['placeholder', 'aria-label', 'title'].forEach((name) => {
          if (!element.hasAttribute(name)) return;
          const current = element.getAttribute(name);
          const saved = state[name];
          const original = saved && (current === saved.output || current === saved.original) ? saved.original : current;
          const output = translate(original);
          element.setAttribute(name, output);
          state[name] = { original, output };
        });
        attributeState.set(element, state);
      });
      document.title = translate(originalTitle);
      observer?.observe(document.body, { subtree: true, childList: true, characterData: true });
    };

    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    try { localStorage.setItem('interface-language', language); } catch { /* Storage is optional. */ }
    observer = new MutationObserver(applyTranslations);
    applyTranslations();
    return () => {
      observer.disconnect();
    };
  }, [language]);

  const arabic = language === 'ar';
  return <button type="button" className="language-toggle" data-no-translate aria-label={arabic ? 'Switch to English' : 'التبديل إلى العربية'} onClick={() => setLanguage(arabic ? 'en' : 'ar')}>{arabic ? 'English' : 'العربية'}</button>;
}
